import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
    Users, 
    CheckCircle, 
    XCircle, 
    Clock, 
    Building2,
    Mail,
    Phone,
    Globe,
    FileText,
    Pencil,
    Eye,
    MapPin,
    Truck,
    Loader2,
    Plus,
    Trash2,
    Save,
    Archive,
    RotateCcw,
    AlertTriangle,
    ShoppingBag,
    Wallet,
    ArrowUpCircle,
    ArrowDownCircle,
    Calendar,
    Search,
    X,
    User,
    Wrench,
    MessageSquare,
    ClipboardList,
    Activity,
    Circle,
    LogIn,
    ExternalLink
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Jamais';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const AdminClients = () => {
    const { token } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState({});
    
    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [activeTab, setActiveTab] = useState('info');
    
    // Delete confirmation modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Search
    const [searchTerm, setSearchTerm] = useState('');
    
    // User credits and wallet
    const [userCredits, setUserCredits] = useState({ total_credit: 0, active_credits: [], expired_credits: [] });
    const [userWallet, setUserWallet] = useState({ balance: 0, transactions: [], active_credits: [] });
    const [loadingCredits, setLoadingCredits] = useState(false);
    
    // Wallet adjustment modal
    const [showWalletAdjust, setShowWalletAdjust] = useState(false);
    const [walletAdjustData, setWalletAdjustData] = useState({
        amount: '',
        operation: 'add',
        reason: '',
        notes: ''
    });
    const [adjustingWallet, setAdjustingWallet] = useState(false);
    
    // Impersonation state
    const [impersonating, setImpersonating] = useState(null);
    
    // Clear cart state
    const [clearCartModalOpen, setClearCartModalOpen] = useState(false);
    const [userToClearCart, setUserToClearCart] = useState(null);
    const [clearingCart, setClearingCart] = useState(false);
    
    // User activity tracking
    const [clientsActivity, setClientsActivity] = useState({});
    const [selectedUserActivity, setSelectedUserActivity] = useState(null);

    // Online users tracking
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [onlineCount, setOnlineCount] = useState(0);

    const statusFilter = searchParams.get('status') || '';

    // Filter users by search term and status
    const filteredUsers = users.filter(user => {
        // Status filter
        if (statusFilter && user.status !== statusFilter) {
            return false;
        }
        
        // Search filter (name, email, company)
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            const matchesCompany = user.company_name?.toLowerCase().includes(search);
            const matchesEmail = user.email?.toLowerCase().includes(search);
            const matchesContact = user.contact_name?.toLowerCase().includes(search);
            const matchesPhone = user.phone?.includes(search);
            
            if (!matchesCompany && !matchesEmail && !matchesContact && !matchesPhone) {
                return false;
            }
        }
        
        return true;
    });

    const countries = [
        { code: 'FR', name: 'France' },
        { code: 'BE', name: 'Belgique' },
        { code: 'DE', name: 'Allemagne' },
        { code: 'ES', name: 'Espagne' },
        { code: 'IT', name: 'Italie' },
        { code: 'NL', name: 'Pays-Bas' },
        { code: 'PT', name: 'Portugal' },
        { code: 'CH', name: 'Suisse' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'GB', name: 'Royaume-Uni' }
    ];

    const fetchUsers = useCallback(async () => {
        try {
            const params = statusFilter ? `?status=${statusFilter}` : '';
            const response = await axios.get(`${API_URL}/admin/users${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data.users);
        } catch (error) {
            console.error('Fetch users error:', error);
            toast.error('Erreur lors du chargement des clients');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    // Fetch clients activity stats
    const fetchClientsActivity = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/clients/activity`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Convert array to object keyed by user_id
            const activityMap = {};
            response.data.forEach(item => {
                activityMap[item.user_id] = item;
            });
            setClientsActivity(activityMap);
        } catch (error) {
            console.error('Fetch activity error:', error);
        }
    }, [token]);

    // Fetch online users
    const fetchOnlineUsers = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/users/online`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOnlineUsers(response.data.online_users || []);
            setOnlineCount(response.data.count || 0);
        } catch (error) {
            console.error('Fetch online users error:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchUsers();
        fetchClientsActivity();
        fetchOnlineUsers();
        
        // Refresh activity every 30 seconds
        const interval = setInterval(fetchClientsActivity, 30000);
        // Refresh online users every 10 seconds
        const onlineInterval = setInterval(fetchOnlineUsers, 10000);
        return () => {
            clearInterval(interval);
            clearInterval(onlineInterval);
        };
    }, [fetchUsers, fetchClientsActivity, fetchOnlineUsers]);

    const openUserModal = async (userId) => {
        setModalOpen(true);
        setModalLoading(true);
        setActiveTab('info');
        setUserCredits({ total_credit: 0, active_credits: [], expired_credits: [] });
        setUserWallet({ balance: 0, transactions: [], active_credits: [] });
        
        try {
            const response = await axios.get(`${API_URL}/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const user = response.data.user;
            setSelectedUser(user);
            setEditForm({
                company_name: user.company_name || '',
                contact_name: user.contact_name || '',
                email: user.email || '',
                phone: user.phone || '',
                siren: user.siren || user.registration_number || '',
                vat_number: user.vat_number || '',
                activity_type: user.activity_type || '',
                website: user.website || '',
                country: user.country || 'FR',
                status: user.status || 'pending',
                role: user.role || 'user',
                billing_address: user.billing_address || {
                    street: '',
                    street2: '',
                    postal_code: '',
                    city: '',
                    country: 'FR',
                    phone: ''
                },
                shipping_addresses: user.shipping_addresses || [],
                admin_notes: user.admin_notes || ''
            });
            
            // Fetch user credits
            try {
                const creditsResponse = await axios.get(`${API_URL}/admin/users/${userId}/credits`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserCredits(creditsResponse.data);
            } catch (err) {
                console.error('Error fetching credits:', err);
            }
            
            // Fetch user wallet (transactions)
            try {
                const walletResponse = await axios.get(`${API_URL}/admin/users/${userId}/wallet`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserWallet(walletResponse.data);
            } catch (err) {
                console.error('Error fetching wallet:', err);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des détails');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    // Handle wallet adjustment
    const handleWalletAdjust = async () => {
        if (!walletAdjustData.amount || parseFloat(walletAdjustData.amount) <= 0) {
            toast.error('Veuillez entrer un montant valide');
            return;
        }
        if (!walletAdjustData.reason.trim()) {
            toast.error('Veuillez entrer une raison');
            return;
        }
        
        setAdjustingWallet(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/users/${selectedUser.id}/wallet/adjust`,
                {
                    amount: parseFloat(walletAdjustData.amount),
                    operation: walletAdjustData.operation,
                    reason: walletAdjustData.reason,
                    notes: walletAdjustData.notes || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(response.data.message);
            setShowWalletAdjust(false);
            setWalletAdjustData({ amount: '', operation: 'add', reason: '', notes: '' });
            
            // Refresh wallet data
            const walletResponse = await axios.get(`${API_URL}/admin/users/${selectedUser.id}/wallet`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserWallet(walletResponse.data);
            
            // Update user's balance in the list
            setUsers(users.map(u => 
                u.id === selectedUser.id 
                    ? { ...u, wallet_balance: response.data.new_balance }
                    : u
            ));
            setSelectedUser({ ...selectedUser, wallet_balance: response.data.new_balance });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajustement du wallet');
        } finally {
            setAdjustingWallet(false);
        }
    };

    // Impersonate user - login as this user
    const handleImpersonate = async (userId, userName) => {
        if (!window.confirm(`Voulez-vous vous connecter en tant que "${userName}" ?\n\nVous serez redirigé vers l'espace client et verrez exactement ce que le client voit.`)) {
            return;
        }
        
        setImpersonating(userId);
        try {
            const response = await axios.post(
                `${API_URL}/admin/users/${userId}/impersonate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Store impersonation info
            const impersonationData = {
                originalToken: token,
                originalUser: JSON.parse(localStorage.getItem('user') || '{}'),
                impersonatedUser: response.data.user,
                impersonatedBy: response.data.impersonated_by
            };
            localStorage.setItem('impersonation', JSON.stringify(impersonationData));
            
            // Replace token and user with impersonated user
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify({
                ...response.data.user,
                role: 'user',
                is_impersonation: true,
                impersonated_by: response.data.impersonated_by
            }));
            
            toast.success(response.data.message);
            
            // Redirect to client dashboard
            window.location.href = '/';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la connexion');
        } finally {
            setImpersonating(null);
        }
    };

    // Clear cart functions
    const openClearCartModal = (user) => {
        setUserToClearCart(user);
        setClearCartModalOpen(true);
    };

    const handleClearCart = async () => {
        if (!userToClearCart) return;
        
        setClearingCart(true);
        try {
            const response = await axios.delete(
                `${API_URL}/admin/users/${userToClearCart.id}/cart`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response.data.message || 'Panier vidé avec succès');
            setClearCartModalOpen(false);
            setUserToClearCart(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du vidage du panier');
        } finally {
            setClearingCart(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API_URL}/admin/users/${selectedUser.id}`,
                editForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Utilisateur mis à jour');
            setModalOpen(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleValidation = async (userId, action) => {
        setProcessing(prev => ({ ...prev, [userId]: true }));
        try {
            await axios.post(
                `${API_URL}/admin/users/validate`,
                { user_id: userId, action },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Compte ${action === 'approve' ? 'approuvé' : 'refusé'}`);
            fetchUsers();
        } catch (error) {
            toast.error('Erreur lors de la validation');
        } finally {
            setProcessing(prev => ({ ...prev, [userId]: false }));
        }
    };

    const updateBillingAddress = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            billing_address: {
                ...prev.billing_address,
                [field]: value
            }
        }));
    };

    const addShippingAddress = () => {
        setEditForm(prev => ({
            ...prev,
            shipping_addresses: [
                ...prev.shipping_addresses,
                {
                    id: `new-${Date.now()}`,
                    name: '',
                    street: '',
                    street2: '',
                    postal_code: '',
                    city: '',
                    country: 'FR',
                    phone: '',
                    is_default: prev.shipping_addresses.length === 0
                }
            ]
        }));
    };

    const updateShippingAddress = (index, field, value) => {
        setEditForm(prev => ({
            ...prev,
            shipping_addresses: prev.shipping_addresses.map((addr, i) => 
                i === index ? { ...addr, [field]: value } : addr
            )
        }));
    };

    const removeShippingAddress = (index) => {
        setEditForm(prev => ({
            ...prev,
            shipping_addresses: prev.shipping_addresses.filter((_, i) => i !== index)
        }));
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
            case 'approved':
                return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Approuvé</Badge>;
            case 'rejected':
                return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Refusé</Badge>;
            case 'archived':
                return <Badge className="bg-slate-100 text-slate-600"><Archive className="w-3 h-3 mr-1" />Archivé</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const openDeleteModal = (user) => {
        setUserToDelete(user);
        setDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        
        setDeleting(true);
        try {
            const response = await axios.delete(
                `${API_URL}/admin/users/${userToDelete.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.had_orders) {
                toast.success(`Client archivé (${response.data.orders_count} commande(s) conservée(s))`);
            } else {
                toast.success('Client archivé avec succès');
            }
            
            setDeleteModalOpen(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
        } finally {
            setDeleting(false);
        }
    };

    const handleRestore = async (userId) => {
        setProcessing(prev => ({ ...prev, [userId]: true }));
        try {
            await axios.put(
                `${API_URL}/admin/users/${userId}/restore`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Client restauré avec succès');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
        } finally {
            setProcessing(prev => ({ ...prev, [userId]: false }));
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Gestion des clients B2B
                        </h1>
                        <p className="text-slate-600 mt-1">
                            {filteredUsers.length} / {users.length} client(s)
                        </p>
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto">
                        {/* Search input */}
                        <div className="relative flex-1 md:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Rechercher par nom, email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-10 h-10"
                                data-testid="search-clients"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Status filter */}
                        <Select 
                            value={statusFilter || 'all'} 
                            onValueChange={(value) => setSearchParams(value && value !== 'all' ? { status: value } : {})}
                        >
                            <SelectTrigger className="w-40" data-testid="status-filter">
                                <SelectValue placeholder="Tous" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                <SelectItem value="pending">En attente</SelectItem>
                                <SelectItem value="approved">Approuvés</SelectItem>
                                <SelectItem value="rejected">Refusés</SelectItem>
                                <SelectItem value="archived">Archivés</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Online Users Banner */}
                {onlineCount > 0 && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                </span>
                                <span className="font-semibold text-green-800">
                                    {onlineCount} client{onlineCount > 1 ? 's' : ''} en ligne
                                </span>
                            </div>
                            <div className="h-4 w-px bg-green-300"></div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {onlineUsers.slice(0, 5).map((user, idx) => (
                                    <div 
                                        key={user.id || idx}
                                        className="flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full border border-green-200 shadow-sm"
                                    >
                                        <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                                        <span className="text-sm font-medium text-slate-700">
                                            {user.company_name}
                                        </span>
                                        <span className="text-xs text-green-600">
                                            {user.status_text || 'En ligne'}
                                        </span>
                                    </div>
                                ))}
                                {onlineCount > 5 && (
                                    <span className="text-sm text-green-600 font-medium">
                                        +{onlineCount - 5} autre{onlineCount - 5 > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <Card key={i} className="border-slate-200">
                                <CardContent className="p-6">
                                    <div className="animate-pulse space-y-4">
                                        <div className="h-6 bg-slate-200 rounded w-1/3"></div>
                                        <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                                        <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-16">
                        <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">
                            {searchTerm ? 'Aucun résultat' : 'Aucun client'}
                        </h2>
                        <p className="text-slate-600">
                            {searchTerm 
                                ? `Aucun client ne correspond à "${searchTerm}"` 
                                : 'Aucun client ne correspond aux filtres'
                            }
                        </p>
                        {searchTerm && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSearchTerm('')}
                                className="mt-4"
                            >
                                Effacer la recherche
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredUsers.map((user) => (
                            <Card key={user.id} className="border-slate-200" data-testid={`user-${user.id}`}>
                                <CardContent className="p-6">
                                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                                        <div className="flex-1 space-y-4">
                                            {/* Header */}
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-3 mb-1">
                                                        {/* Online Status Indicator */}
                                                        {clientsActivity[user.id]?.is_online ? (
                                                            <span className="relative flex h-3 w-3" title="En ligne">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                                            </span>
                                                        ) : (
                                                            <span className="relative flex h-3 w-3" title="Hors ligne">
                                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-slate-300"></span>
                                                            </span>
                                                        )}
                                                        <h3 className="font-semibold text-lg text-slate-900">{user.company_name}</h3>
                                                        {getStatusBadge(user.status)}
                                                        {user.role === 'admin' && (
                                                            <Badge className="bg-slate-900 text-white">Admin</Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                                        <span>Inscrit le {formatDate(user.created_at)}</span>
                                                        <span className="flex items-center gap-1">
                                                            <Activity className="w-3 h-3" />
                                                            {clientsActivity[user.id]?.last_seen ? (
                                                                <>Vu {formatRelativeTime(clientsActivity[user.id].last_seen)}</>
                                                            ) : (
                                                                <>Jamais connecté</>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Activity Stats Badge */}
                                                {clientsActivity[user.id] && clientsActivity[user.id].logins_month > 0 && (
                                                    <div className="hidden sm:flex items-center gap-2 text-xs">
                                                        <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1" title="Connexions ce mois">
                                                            <Activity className="w-3 h-3" />
                                                            {clientsActivity[user.id].logins_month} ce mois
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Details */}
                                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="w-4 h-4 text-slate-400" />
                                                    <div>
                                                        <p className="text-slate-500">Contact</p>
                                                        <p className="font-medium">{user.contact_name}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4 text-slate-400" />
                                                    <div>
                                                        <p className="text-slate-500">Email</p>
                                                        <p className="font-medium">{user.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4 text-slate-400" />
                                                    <div>
                                                        <p className="text-slate-500">Téléphone</p>
                                                        <p className="font-medium">{user.phone || '-'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-slate-400" />
                                                    <div>
                                                        <p className="text-slate-500">Activité</p>
                                                        <p className="font-medium">{user.activity_type || '-'}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Business Info */}
                                            <div className="flex flex-wrap gap-4 text-sm">
                                                {user.vat_number && (
                                                    <div className="bg-slate-50 rounded px-3 py-2">
                                                        <span className="text-slate-500">TVA: </span>
                                                        <span className="font-mono font-medium">{user.vat_number}</span>
                                                    </div>
                                                )}
                                                {(user.siren || user.registration_number) && (
                                                    <div className="bg-slate-50 rounded px-3 py-2">
                                                        <span className="text-slate-500">SIREN: </span>
                                                        <span className="font-mono font-medium">{user.siren || user.registration_number}</span>
                                                    </div>
                                                )}
                                                {user.country && (
                                                    <div className="bg-slate-50 rounded px-3 py-2">
                                                        <span className="text-slate-500">Pays: </span>
                                                        <span className="font-medium">{user.country}</span>
                                                    </div>
                                                )}
                                                {user.website && (
                                                    <a 
                                                        href={user.website} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="bg-slate-50 rounded px-3 py-2 flex items-center gap-1 hover:bg-slate-100"
                                                    >
                                                        <Globe className="w-3 h-3" />
                                                        <span className="text-blue-600 hover:underline">Site web</span>
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex gap-2 lg:flex-col">
                                            <Button
                                                variant="outline"
                                                onClick={() => openUserModal(user.id)}
                                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                                data-testid={`edit-${user.id}`}
                                            >
                                                <Pencil className="w-4 h-4 mr-2" />
                                                Modifier
                                            </Button>
                                            {/* Impersonate button - for approved users only */}
                                            {user.status === 'approved' && user.role !== 'admin' && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleImpersonate(user.id, user.company_name || user.email)}
                                                    disabled={impersonating === user.id}
                                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                                    data-testid={`impersonate-${user.id}`}
                                                    title="Se connecter en tant que ce client"
                                                >
                                                    {impersonating === user.id ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <LogIn className="w-4 h-4 mr-2" />
                                                    )}
                                                    Vue Client
                                                </Button>
                                            )}
                                            {/* Clear Cart button - for approved users only */}
                                            {user.status === 'approved' && user.role !== 'admin' && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => openClearCartModal(user)}
                                                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                                    data-testid={`clear-cart-${user.id}`}
                                                    title="Vider le panier de ce client"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Vider Panier
                                                </Button>
                                            )}
                                            {user.status === 'pending' && user.role !== 'admin' && (
                                                <>
                                                    <Button
                                                        onClick={() => handleValidation(user.id, 'approve')}
                                                        disabled={processing[user.id]}
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        data-testid={`approve-${user.id}`}
                                                    >
                                                        <CheckCircle className="w-4 h-4 mr-2" />
                                                        Approuver
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => handleValidation(user.id, 'reject')}
                                                        disabled={processing[user.id]}
                                                        className="text-red-600 border-red-200 hover:bg-red-50"
                                                        data-testid={`reject-${user.id}`}
                                                    >
                                                        <XCircle className="w-4 h-4 mr-2" />
                                                        Refuser
                                                    </Button>
                                                </>
                                            )}
                                            {user.status === 'archived' && user.role !== 'admin' && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleRestore(user.id)}
                                                    disabled={processing[user.id]}
                                                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                                    data-testid={`restore-${user.id}`}
                                                >
                                                    <RotateCcw className="w-4 h-4 mr-2" />
                                                    Restaurer
                                                </Button>
                                            )}
                                            {user.status !== 'archived' && user.role !== 'admin' && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => openDeleteModal(user)}
                                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                                    data-testid={`delete-${user.id}`}
                                                >
                                                    <Archive className="w-4 h-4 mr-2" />
                                                    Archiver
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* User Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-600" />
                            {selectedUser?.company_name || 'Détails utilisateur'}
                        </DialogTitle>
                    </DialogHeader>

                    {modalLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        </div>
                    ) : selectedUser && (
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <TabsList className="grid w-full grid-cols-5">
                                <TabsTrigger value="info" className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    <span className="hidden sm:inline">Infos</span>
                                </TabsTrigger>
                                <TabsTrigger value="profile" className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    <span className="hidden sm:inline">Profil</span>
                                </TabsTrigger>
                                <TabsTrigger value="billing" className="flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    <span className="hidden sm:inline">Facturation</span>
                                </TabsTrigger>
                                <TabsTrigger value="shipping" className="flex items-center gap-2">
                                    <Truck className="w-4 h-4" />
                                    <span className="hidden sm:inline">Livraison</span>
                                </TabsTrigger>
                                <TabsTrigger value="credits" className="flex items-center gap-2">
                                    <Wallet className="w-4 h-4" />
                                    <span className="hidden sm:inline">Avoirs</span>
                                    {userCredits.total_credit > 0 && (
                                        <Badge className="bg-green-100 text-green-700 ml-1">
                                            {userCredits.total_credit.toFixed(0)}€
                                        </Badge>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="activity" className="flex items-center gap-2">
                                    <Activity className="w-4 h-4" />
                                    <span className="hidden sm:inline">Activité</span>
                                    {selectedUser && clientsActivity[selectedUser.id]?.is_online && (
                                        <span className="relative flex h-2 w-2 ml-1">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                        </span>
                                    )}
                                </TabsTrigger>
                            </TabsList>

                            {/* Info Tab */}
                            <TabsContent value="info" className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nom de l'entreprise *</Label>
                                        <Input
                                            value={editForm.company_name}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
                                            data-testid="edit-company-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Nom du contact *</Label>
                                        <Input
                                            value={editForm.contact_name}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, contact_name: e.target.value }))}
                                            data-testid="edit-contact-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email *</Label>
                                        <Input
                                            type="email"
                                            value={editForm.email}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                                            data-testid="edit-email"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Téléphone</Label>
                                        <Input
                                            value={editForm.phone}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                            data-testid="edit-phone"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>SIREN</Label>
                                        <Input
                                            value={editForm.siren}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, siren: e.target.value }))}
                                            data-testid="edit-siren"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>N° TVA</Label>
                                        <Input
                                            value={editForm.vat_number}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, vat_number: e.target.value }))}
                                            data-testid="edit-vat"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Type d'activité</Label>
                                        <Input
                                            value={editForm.activity_type}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, activity_type: e.target.value }))}
                                            placeholder="Revendeur, Réparateur..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Site web</Label>
                                        <Input
                                            value={editForm.website}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))}
                                            placeholder="https://..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Pays</Label>
                                        <Select
                                            value={editForm.country}
                                            onValueChange={(value) => setEditForm(prev => ({ ...prev, country: value }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {countries.map((c) => (
                                                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Statut</Label>
                                        <Select
                                            value={editForm.status}
                                            onValueChange={(value) => setEditForm(prev => ({ ...prev, status: value }))}
                                        >
                                            <SelectTrigger data-testid="edit-status">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="pending">En attente</SelectItem>
                                                <SelectItem value="approved">Approuvé</SelectItem>
                                                <SelectItem value="rejected">Refusé</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Rôle</Label>
                                        <Select
                                            value={editForm.role}
                                            onValueChange={(value) => setEditForm(prev => ({ ...prev, role: value }))}
                                        >
                                            <SelectTrigger data-testid="edit-role">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="user">Utilisateur</SelectItem>
                                                <SelectItem value="admin">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* Profile Tab - Registration answers & Admin notes */}
                            <TabsContent value="profile" className="space-y-4 mt-4">
                                {/* Activity & Needs from registration */}
                                <Card className="border-slate-200">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <ClipboardList className="w-4 h-4 text-blue-600" />
                                            Réponses à l'inscription
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <Label className="text-xs text-slate-500">Type d'activité</Label>
                                                <p className="text-sm font-medium">
                                                    {selectedUser?.activity_types?.length > 0 
                                                        ? selectedUser.activity_types.join(', ')
                                                        : selectedUser?.activity_type || '-'}
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-slate-500">Recherche principale</Label>
                                                <p className="text-sm font-medium">
                                                    {selectedUser?.search_preference === 'appareils_reparer' && 'Appareils à réparer'}
                                                    {selectedUser?.search_preference === 'fonctionnels' && 'Appareils fonctionnels'}
                                                    {selectedUser?.search_preference === 'les_deux' && 'Les deux'}
                                                    {!selectedUser?.search_preference && '-'}
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-slate-500">Niveau de réparation</Label>
                                                <p className="text-sm font-medium">
                                                    {selectedUser?.repair_level || '-'}
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-slate-500">Volume estimé / semaine</Label>
                                                <p className="text-sm font-medium">
                                                    {selectedUser?.weekly_volume === 'moins_10' && 'Moins de 10'}
                                                    {selectedUser?.weekly_volume === '10_30' && '10 à 30'}
                                                    {selectedUser?.weekly_volume === '30_100' && '30 à 100'}
                                                    {selectedUser?.weekly_volume === 'plus_100' && 'Plus de 100'}
                                                    {!selectedUser?.weekly_volume && '-'}
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-slate-500">Points de vente</Label>
                                                <p className="text-sm font-medium">
                                                    {selectedUser?.points_of_sale === '1' && '1 point de vente'}
                                                    {selectedUser?.points_of_sale === '2_5' && '2 à 5 points de vente'}
                                                    {selectedUser?.points_of_sale === 'plus_5' && 'Plus de 5'}
                                                    {selectedUser?.points_of_sale === 'pure_player' && 'Pure player (en ligne)'}
                                                    {!selectedUser?.points_of_sale && '-'}
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-slate-500">WhatsApp</Label>
                                                <p className="text-sm font-medium">{selectedUser?.whatsapp || '-'}</p>
                                            </div>
                                        </div>
                                        
                                        {selectedUser?.comments && (
                                            <div className="space-y-1 pt-2 border-t">
                                                <Label className="text-xs text-slate-500">Commentaire du client</Label>
                                                <p className="text-sm bg-slate-50 p-3 rounded-md italic">
                                                    "{selectedUser.comments}"
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Registration consent */}
                                        {selectedUser?.consent && (
                                            <div className="space-y-1 pt-2 border-t">
                                                <Label className="text-xs text-slate-500">Consentements à l'inscription</Label>
                                                <div className="text-xs text-slate-600 space-y-1">
                                                    <p>CGV acceptées : {selectedUser.consent.accept_cgv ? '✓ Oui' : '✗ Non'}</p>
                                                    <p>Contact commercial : {selectedUser.consent.accept_contact ? '✓ Oui' : '✗ Non'}</p>
                                                    <p>Date : {selectedUser.consent.timestamp ? new Date(selectedUser.consent.timestamp).toLocaleString('fr-FR') : '-'}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Cookie consent */}
                                        {selectedUser?.cookie_consent && (
                                            <div className="space-y-1 pt-2 border-t">
                                                <Label className="text-xs text-slate-500">Consentement cookies (RGPD)</Label>
                                                <div className="text-xs text-slate-600 space-y-1">
                                                    <p>Cookies essentiels : <span className="text-emerald-600 font-medium">✓ Toujours actifs</span></p>
                                                    <p>Cookies analytiques : {selectedUser.cookie_consent.analytics ? 
                                                        <span className="text-emerald-600 font-medium">✓ Acceptés</span> : 
                                                        <span className="text-slate-500">✗ Refusés</span>
                                                    }</p>
                                                    <p>Cookies marketing : {selectedUser.cookie_consent.marketing ? 
                                                        <span className="text-emerald-600 font-medium">✓ Acceptés</span> : 
                                                        <span className="text-slate-500">✗ Refusés</span>
                                                    }</p>
                                                    <p>Tout accepté : {selectedUser.cookie_consent.accepted_all ? 
                                                        <span className="text-emerald-600 font-medium">✓ Oui</span> : 
                                                        <span className="text-slate-500">✗ Non (choix personnalisé)</span>
                                                    }</p>
                                                    <p>Date : {selectedUser.cookie_consent.timestamp ? 
                                                        new Date(selectedUser.cookie_consent.timestamp).toLocaleString('fr-FR') : '-'}</p>
                                                    <p className="text-slate-400">Version : {selectedUser.cookie_consent.version || '-'}</p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {!selectedUser?.cookie_consent && (
                                            <div className="space-y-1 pt-2 border-t">
                                                <Label className="text-xs text-slate-500">Consentement cookies (RGPD)</Label>
                                                <p className="text-xs text-slate-400 italic">Pas encore enregistré (visiteur non connecté lors du choix)</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Confidential Admin Notes */}
                                <Card className="border-amber-200 bg-amber-50/30">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <MessageSquare className="w-4 h-4 text-amber-600" />
                                            Notes confidentielles (Admin uniquement)
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <Textarea
                                            value={editForm.admin_notes || ''}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                                            placeholder="Notes internes sur ce client (visible uniquement par les admins)..."
                                            rows={5}
                                            className="bg-white"
                                            data-testid="admin-notes"
                                        />
                                        <p className="text-xs text-amber-700">
                                            Ces notes ne sont jamais visibles par le client.
                                        </p>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Billing Tab */}
                            <TabsContent value="billing" className="space-y-4 mt-4">
                                <Card className="border-slate-200">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-blue-600" />
                                            Adresse de facturation
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Adresse *</Label>
                                            <Input
                                                value={editForm.billing_address?.street || ''}
                                                onChange={(e) => updateBillingAddress('street', e.target.value)}
                                                placeholder="123 rue de la Facturation"
                                                data-testid="billing-street"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Complément</Label>
                                            <Input
                                                value={editForm.billing_address?.street2 || ''}
                                                onChange={(e) => updateBillingAddress('street2', e.target.value)}
                                                placeholder="Bâtiment, étage..."
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Code postal *</Label>
                                                <Input
                                                    value={editForm.billing_address?.postal_code || ''}
                                                    onChange={(e) => updateBillingAddress('postal_code', e.target.value)}
                                                    placeholder="75001"
                                                    data-testid="billing-postal"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Ville *</Label>
                                                <Input
                                                    value={editForm.billing_address?.city || ''}
                                                    onChange={(e) => updateBillingAddress('city', e.target.value)}
                                                    placeholder="Paris"
                                                    data-testid="billing-city"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Pays</Label>
                                                <Select
                                                    value={editForm.billing_address?.country || 'FR'}
                                                    onValueChange={(value) => updateBillingAddress('country', value)}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {countries.map((c) => (
                                                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Téléphone</Label>
                                                <Input
                                                    value={editForm.billing_address?.phone || ''}
                                                    onChange={(e) => updateBillingAddress('phone', e.target.value)}
                                                    placeholder="+33 1 00 00 00 00"
                                                />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Shipping Tab */}
                            <TabsContent value="shipping" className="space-y-4 mt-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-medium text-slate-900">Adresses de livraison</h3>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={addShippingAddress}
                                        data-testid="add-shipping-address"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter
                                    </Button>
                                </div>

                                {editForm.shipping_addresses?.length === 0 ? (
                                    <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                                        <Truck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Aucune adresse de livraison</p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={addShippingAddress}
                                            className="mt-3"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Ajouter une adresse
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {editForm.shipping_addresses?.map((addr, index) => (
                                            <Card key={addr.id || index} className="border-slate-200">
                                                <CardHeader className="pb-2">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                value={addr.name || ''}
                                                                onChange={(e) => updateShippingAddress(index, 'name', e.target.value)}
                                                                placeholder="Nom / Libellé (ex: Entrepôt)"
                                                                className="w-48 h-8"
                                                            />
                                                            {addr.is_default && (
                                                                <Badge className="bg-blue-100 text-blue-700">Par défaut</Badge>
                                                            )}
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeShippingAddress(index)}
                                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    <div className="space-y-2">
                                                        <Label className="text-xs">Adresse</Label>
                                                        <Input
                                                            value={addr.street || ''}
                                                            onChange={(e) => updateShippingAddress(index, 'street', e.target.value)}
                                                            placeholder="123 rue de la Livraison"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label className="text-xs">Complément</Label>
                                                        <Input
                                                            value={addr.street2 || ''}
                                                            onChange={(e) => updateShippingAddress(index, 'street2', e.target.value)}
                                                            placeholder="Bâtiment, étage..."
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-3">
                                                        <div className="space-y-2">
                                                            <Label className="text-xs">Code postal</Label>
                                                            <Input
                                                                value={addr.postal_code || ''}
                                                                onChange={(e) => updateShippingAddress(index, 'postal_code', e.target.value)}
                                                                placeholder="75001"
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label className="text-xs">Ville</Label>
                                                            <Input
                                                                value={addr.city || ''}
                                                                onChange={(e) => updateShippingAddress(index, 'city', e.target.value)}
                                                                placeholder="Paris"
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label className="text-xs">Pays</Label>
                                                            <Select
                                                                value={addr.country || 'FR'}
                                                                onValueChange={(value) => updateShippingAddress(index, 'country', value)}
                                                            >
                                                                <SelectTrigger className="h-9">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {countries.map((c) => (
                                                                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label className="text-xs">Téléphone</Label>
                                                        <Input
                                                            value={addr.phone || ''}
                                                            onChange={(e) => updateShippingAddress(index, 'phone', e.target.value)}
                                                            placeholder="+33 6 00 00 00 00"
                                                        />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>

                            {/* Credits/Avoirs Tab */}
                            <TabsContent value="credits" className="space-y-4 mt-4">
                                {/* Balance Banner with Adjust Button */}
                                <Card className="border-green-200 bg-green-50">
                                    <CardContent className="py-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-green-100 rounded-full">
                                                    <Wallet className="w-6 h-6 text-green-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-green-700">Crédit disponible</p>
                                                    <p className="text-2xl font-bold text-green-800">
                                                        {(userWallet.balance || userCredits.total_credit || 0).toFixed(2)} €
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setShowWalletAdjust(true)}
                                                className="border-green-300 text-green-700 hover:bg-green-100"
                                            >
                                                <Pencil className="w-4 h-4 mr-2" />
                                                Ajuster
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Wallet Adjustment Modal */}
                                <Dialog open={showWalletAdjust} onOpenChange={setShowWalletAdjust}>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center gap-2">
                                                <Wallet className="w-5 h-5 text-green-600" />
                                                Ajuster le Wallet
                                            </DialogTitle>
                                            <DialogDescription>
                                                Ajouter ou retirer du crédit manuellement pour {selectedUser?.company_name || selectedUser?.email}
                                            </DialogDescription>
                                        </DialogHeader>
                                        
                                        <div className="space-y-4 py-4">
                                            {/* Current Balance */}
                                            <div className="p-3 bg-slate-100 rounded-lg text-center">
                                                <p className="text-sm text-slate-500">Solde actuel</p>
                                                <p className="text-2xl font-bold">{(userWallet.balance || 0).toFixed(2)} €</p>
                                            </div>
                                            
                                            {/* Operation Type */}
                                            <div className="space-y-2">
                                                <Label>Opération</Label>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        variant={walletAdjustData.operation === 'add' ? 'default' : 'outline'}
                                                        className={walletAdjustData.operation === 'add' ? 'bg-green-600 hover:bg-green-700' : ''}
                                                        onClick={() => setWalletAdjustData({...walletAdjustData, operation: 'add'})}
                                                    >
                                                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                                                        Créditer
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant={walletAdjustData.operation === 'subtract' ? 'default' : 'outline'}
                                                        className={walletAdjustData.operation === 'subtract' ? 'bg-red-600 hover:bg-red-700' : ''}
                                                        onClick={() => setWalletAdjustData({...walletAdjustData, operation: 'subtract'})}
                                                    >
                                                        <ArrowDownCircle className="w-4 h-4 mr-2" />
                                                        Débiter
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Amount */}
                                            <div className="space-y-2">
                                                <Label htmlFor="wallet-amount">Montant (€)</Label>
                                                <Input
                                                    id="wallet-amount"
                                                    type="number"
                                                    step="0.01"
                                                    min="0.01"
                                                    placeholder="0.00"
                                                    value={walletAdjustData.amount}
                                                    onChange={(e) => setWalletAdjustData({...walletAdjustData, amount: e.target.value})}
                                                    className="text-lg"
                                                />
                                            </div>
                                            
                                            {/* Reason */}
                                            <div className="space-y-2">
                                                <Label htmlFor="wallet-reason">Raison *</Label>
                                                <Select 
                                                    value={walletAdjustData.reason} 
                                                    onValueChange={(val) => setWalletAdjustData({...walletAdjustData, reason: val})}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Sélectionner une raison" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Geste commercial">Geste commercial</SelectItem>
                                                        <SelectItem value="Remboursement commande">Remboursement commande</SelectItem>
                                                        <SelectItem value="Correction erreur">Correction erreur</SelectItem>
                                                        <SelectItem value="Prime fidélité">Prime fidélité</SelectItem>
                                                        <SelectItem value="Avoir produit défectueux">Avoir produit défectueux</SelectItem>
                                                        <SelectItem value="Ajustement inventaire">Ajustement inventaire</SelectItem>
                                                        <SelectItem value="Retrait non autorisé">Retrait non autorisé</SelectItem>
                                                        <SelectItem value="Autre">Autre</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Notes */}
                                            <div className="space-y-2">
                                                <Label htmlFor="wallet-notes">Notes (optionnel)</Label>
                                                <Textarea
                                                    id="wallet-notes"
                                                    placeholder="Détails supplémentaires..."
                                                    value={walletAdjustData.notes}
                                                    onChange={(e) => setWalletAdjustData({...walletAdjustData, notes: e.target.value})}
                                                    rows={2}
                                                />
                                            </div>
                                            
                                            {/* Preview */}
                                            {walletAdjustData.amount && (
                                                <div className={`p-3 rounded-lg border ${
                                                    walletAdjustData.operation === 'add' 
                                                        ? 'bg-green-50 border-green-200' 
                                                        : 'bg-red-50 border-red-200'
                                                }`}>
                                                    <p className="text-sm text-center">
                                                        Nouveau solde après opération:
                                                        <span className="font-bold text-lg block">
                                                            {walletAdjustData.operation === 'add'
                                                                ? ((userWallet.balance || 0) + parseFloat(walletAdjustData.amount || 0)).toFixed(2)
                                                                : ((userWallet.balance || 0) - parseFloat(walletAdjustData.amount || 0)).toFixed(2)
                                                            } €
                                                        </span>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setShowWalletAdjust(false)}>
                                                Annuler
                                            </Button>
                                            <Button 
                                                onClick={handleWalletAdjust}
                                                disabled={adjustingWallet || !walletAdjustData.amount || !walletAdjustData.reason}
                                                className={walletAdjustData.operation === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                                            >
                                                {adjustingWallet ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : walletAdjustData.operation === 'add' ? (
                                                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                                                ) : (
                                                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                                                )}
                                                {walletAdjustData.operation === 'add' ? 'Créditer' : 'Débiter'}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                {/* Transaction History */}
                                {userWallet.transactions?.length > 0 ? (
                                    <div>
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-slate-500" />
                                            Historique des mouvements
                                        </h4>
                                        <div className="space-y-2 max-h-80 overflow-y-auto">
                                            {userWallet.transactions.map((tx, idx) => (
                                                <div 
                                                    key={tx.id || idx} 
                                                    className={`p-3 rounded-lg border ${
                                                        tx.type === 'credit' 
                                                            ? 'bg-green-50/50 border-green-100' 
                                                            : 'bg-red-50/30 border-red-100'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {tx.type === 'credit' ? (
                                                                <ArrowUpCircle className="w-5 h-5 text-green-600" />
                                                            ) : (
                                                                <ArrowDownCircle className="w-5 h-5 text-red-600" />
                                                            )}
                                                            <div>
                                                                <p className="font-medium text-sm">
                                                                    {tx.reference_number}
                                                                </p>
                                                                <p className="text-xs text-slate-500">
                                                                    {tx.description}
                                                                </p>
                                                                <p className="text-xs text-slate-400">
                                                                    {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                                                                        day: 'numeric',
                                                                        month: 'short',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className={`font-bold ${
                                                                tx.type === 'credit' ? 'text-green-700' : 'text-red-700'
                                                            }`}>
                                                                {tx.type === 'credit' ? '+' : '-'}{tx.amount.toFixed(2)} €
                                                            </p>
                                                            <p className="text-xs text-slate-500">
                                                                Solde: {tx.balance_after.toFixed(2)} €
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : userCredits.total_credit > 0 ? (
                                    /* Fallback to old credits display if no transactions */
                                    <div>
                                        <h4 className="font-medium mb-3">Avoirs actifs</h4>
                                        <div className="space-y-2">
                                            {userCredits.active_credits.map((credit, idx) => (
                                                <Card key={idx} className="border-slate-200">
                                                    <CardContent className="py-3">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="font-medium text-sm">
                                                                    {credit.reason || 'Avoir SAV'}
                                                                </p>
                                                                <p className="text-xs text-slate-500">
                                                                    Créé le {new Date(credit.created_at).toLocaleDateString('fr-FR')}
                                                                </p>
                                                                <p className="text-xs text-slate-500">
                                                                    Expire le {new Date(credit.expires_at).toLocaleDateString('fr-FR')}
                                                                </p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="font-bold text-green-600">
                                                                    {credit.remaining_amount?.toFixed(2)} €
                                                                </p>
                                                                {credit.original_amount !== credit.remaining_amount && (
                                                                    <p className="text-xs text-slate-400">
                                                                        sur {credit.original_amount?.toFixed(2)} €
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-slate-500">
                                        <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                        <p>Aucun mouvement pour ce client</p>
                                    </div>
                                )}

                                {/* Active Credits Detail */}
                                {userWallet.active_credits?.length > 0 && (
                                    <div className="mt-4 pt-4 border-t">
                                        <h4 className="font-medium mb-3 text-slate-600">Avoirs actifs</h4>
                                        <div className="space-y-2">
                                            {userWallet.active_credits.map((credit, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                                                    <div>
                                                        <span className="text-green-800">{credit.reason || 'Avoir'}</span>
                                                        <p className="text-xs text-green-600">
                                                            Expire le {new Date(credit.expires_at).toLocaleDateString('fr-FR')}
                                                        </p>
                                                    </div>
                                                    <span className="font-bold text-green-700">{credit.remaining_amount?.toFixed(2)} €</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            {/* Activity Tab */}
                            <TabsContent value="activity" className="space-y-4 mt-4">
                                {selectedUser && (
                                    <div className="space-y-6">
                                        {/* Online Status */}
                                        <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                                            {clientsActivity[selectedUser.id]?.is_online ? (
                                                <>
                                                    <span className="relative flex h-4 w-4">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
                                                    </span>
                                                    <div>
                                                        <p className="font-semibold text-green-700">En ligne</p>
                                                        <p className="text-sm text-slate-500">Actuellement actif sur le site</p>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="relative flex h-4 w-4">
                                                        <span className="relative inline-flex rounded-full h-4 w-4 bg-slate-300"></span>
                                                    </span>
                                                    <div>
                                                        <p className="font-semibold text-slate-600">Hors ligne</p>
                                                        <p className="text-sm text-slate-500">
                                                            Dernière visite: {clientsActivity[selectedUser.id]?.last_seen 
                                                                ? formatRelativeTime(clientsActivity[selectedUser.id].last_seen) 
                                                                : 'Jamais'}
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Connection Stats */}
                                        <div>
                                            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                                <Calendar className="w-4 h-4" />
                                                Statistiques de connexion
                                            </h4>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="bg-blue-50 rounded-lg p-4 text-center">
                                                    <p className="text-3xl font-bold text-blue-600">
                                                        {clientsActivity[selectedUser.id]?.logins_today || 0}
                                                    </p>
                                                    <p className="text-sm text-blue-600 mt-1">Aujourd'hui</p>
                                                </div>
                                                <div className="bg-indigo-50 rounded-lg p-4 text-center">
                                                    <p className="text-3xl font-bold text-indigo-600">
                                                        {clientsActivity[selectedUser.id]?.logins_week || 0}
                                                    </p>
                                                    <p className="text-sm text-indigo-600 mt-1">Cette semaine</p>
                                                </div>
                                                <div className="bg-violet-50 rounded-lg p-4 text-center">
                                                    <p className="text-3xl font-bold text-violet-600">
                                                        {clientsActivity[selectedUser.id]?.logins_month || 0}
                                                    </p>
                                                    <p className="text-sm text-violet-600 mt-1">Ce mois</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Last Login */}
                                        {clientsActivity[selectedUser.id]?.last_login && (
                                            <div className="border-t pt-4">
                                                <p className="text-sm text-slate-500">
                                                    <span className="font-medium">Dernière connexion:</span>{' '}
                                                    {new Date(clientsActivity[selectedUser.id].last_login).toLocaleString('fr-FR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </p>
                                            </div>
                                        )}

                                        {/* No Activity Message */}
                                        {!clientsActivity[selectedUser.id]?.last_login && (
                                            <div className="text-center py-8 text-slate-400">
                                                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                                <p>Aucune activité enregistrée</p>
                                                <p className="text-sm">Ce client ne s'est pas encore connecté</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    )}

                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setModalOpen(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="save-user"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Archiver ce client ?
                        </DialogTitle>
                        <DialogDescription className="text-slate-600">
                            Cette action va désactiver le compte du client.
                        </DialogDescription>
                    </DialogHeader>

                    {userToDelete && (
                        <div className="py-4">
                            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{userToDelete.company_name}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <Mail className="w-4 h-4 text-slate-400" />
                                    <span>{userToDelete.email}</span>
                                </div>
                            </div>

                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <ShoppingBag className="w-4 h-4 text-amber-600 mt-0.5" />
                                    <div className="text-sm text-amber-800">
                                        <p className="font-medium">Note importante</p>
                                        <p>Si ce client a des commandes, elles seront conservées dans l'historique. Le client ne pourra plus se connecter.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setDeleteModalOpen(false)}
                            disabled={deleting}
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleDelete}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete"
                        >
                            {deleting ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Archive className="w-4 h-4 mr-2" />
                            )}
                            Archiver le client
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Clear Cart Confirmation Modal */}
            <Dialog open={clearCartModalOpen} onOpenChange={setClearCartModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-orange-600">
                            <Trash2 className="w-5 h-5" />
                            Vider le panier ?
                        </DialogTitle>
                        <DialogDescription className="text-slate-600">
                            Cette action va supprimer tous les articles du panier de ce client et libérer les réservations ERP.
                        </DialogDescription>
                    </DialogHeader>

                    {userToClearCart && (
                        <div className="py-4">
                            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{userToClearCart.company_name}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <Mail className="w-4 h-4 text-slate-400" />
                                    <span>{userToClearCart.email}</span>
                                </div>
                            </div>

                            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <ShoppingBag className="w-4 h-4 text-orange-600 mt-0.5" />
                                    <div className="text-sm text-orange-800">
                                        <p className="font-medium">Cette action est irréversible</p>
                                        <p>Tous les articles du panier seront supprimés et les réservations ERP seront annulées.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setClearCartModalOpen(false)}
                            disabled={clearingCart}
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleClearCart}
                            disabled={clearingCart}
                            className="bg-orange-600 hover:bg-orange-700"
                            data-testid="confirm-clear-cart"
                        >
                            {clearingCart ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Vider le panier
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminClients;
